import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Shield, MessageSquare, Ticket as TicketIcon, Users, Building2, Filter, X } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const TYPE_LABEL = {
    dm: "DM",
    internal_thread: "Interno",
    client_thread: "Canale cliente",
    ticket: "Ticket",
};

const TYPE_ICON = {
    dm: MessageSquare,
    internal_thread: Users,
    client_thread: Building2,
    ticket: TicketIcon,
};

export default function AdminConversations() {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Guard: solo super admin
    useEffect(() => {
        if (user && !user.is_super_admin) {
            toast.error("Accesso riservato ai super admin");
            navigate("/app");
        }
    }, [user, navigate]);

    const [orgs, setOrgs] = useState([]);
    const [filters, setFilters] = useState({ org: "", type: "", q: "" });
    const [rawQ, setRawQ] = useState("");
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const PAGE = 50;

    // Load orgs once
    useEffect(() => {
        api.get("/admin/organizations").then(({ data }) => setOrgs(data.items || [])).catch(() => {});
    }, []);

    // Debounce search
    useEffect(() => {
        const id = setTimeout(() => setFilters((f) => ({ ...f, q: rawQ })), 400);
        return () => clearTimeout(id);
    }, [rawQ]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE, offset };
            if (filters.org) params.org = filters.org;
            if (filters.type) params.type = filters.type;
            if (filters.q) params.q = filters.q;
            const { data } = await api.get("/admin/conversations", { params });
            setItems(data.conversations || []);
            setCount(data.count || 0);
        } catch (err) {
            if (err?.response?.status === 403) {
                toast.error("Accesso riservato ai super admin");
                navigate("/app");
            } else {
                toast.error("Errore caricamento conversazioni");
            }
        } finally { setLoading(false); }
    }, [filters, offset, navigate]);

    useEffect(() => { load(); }, [load]);

    const activeFilters = useMemo(() => {
        const chips = [];
        if (filters.org) {
            const o = orgs.find((x) => x.id === filters.org);
            chips.push({ key: "org", label: `Organizzazione: ${o?.name || filters.org}`, clear: () => setFilters((f) => ({ ...f, org: "" })) });
        }
        if (filters.type) chips.push({ key: "type", label: `Tipo: ${TYPE_LABEL[filters.type] || filters.type}`, clear: () => setFilters((f) => ({ ...f, type: "" })) });
        if (filters.q) chips.push({ key: "q", label: `Ricerca: "${filters.q}"`, clear: () => { setRawQ(""); setFilters((f) => ({ ...f, q: "" })); } });
        return chips;
    }, [filters, orgs]);

    if (user && !user.is_super_admin) return null;

    return (
        <div style={{ padding: "32px 40px", maxWidth: 1280 }} data-testid="admin-conversations-page">
            <div className="flex items-center gap-3" style={{ marginBottom: 8 }}>
                <Shield size={20} color="#181B20" />
                <h1 className="lt-h1" data-testid="admin-conversations-heading">Amministrazione conversazioni</h1>
            </div>
            <p className="lt-sub" style={{ marginTop: 4, marginBottom: 24, maxWidth: 720 }}>
                Vista super admin. Accesso in sola lettura a tutte le conversazioni della piattaforma. Ogni apertura è registrata nel log di audit.
            </p>

            {/* Toolbar */}
            <div className="lt-card" style={{ padding: 16, marginBottom: 20 }}>
                <div className="flex items-center gap-3 flex-wrap">
                    <div style={{ position: "relative", flex: "1 1 240px", minWidth: 220 }}>
                        <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#8A8F88" }} />
                        <input
                            type="search"
                            className="lt-input"
                            placeholder="Cerca in titoli, aziende, ultimo messaggio…"
                            value={rawQ}
                            onChange={(e) => setRawQ(e.target.value)}
                            style={{ paddingLeft: 34, width: "100%" }}
                            data-testid="admin-conv-search"
                        />
                    </div>
                    <select
                        className="lt-input"
                        value={filters.org}
                        onChange={(e) => setFilters((f) => ({ ...f, org: e.target.value }))}
                        data-testid="admin-conv-filter-org"
                        style={{ minWidth: 200 }}
                    >
                        <option value="">Tutte le organizzazioni</option>
                        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <select
                        className="lt-input"
                        value={filters.type}
                        onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
                        data-testid="admin-conv-filter-type"
                        style={{ minWidth: 180 }}
                    >
                        <option value="">Tutti i tipi</option>
                        <option value="internal_thread">Interno (staff)</option>
                        <option value="client_thread">Canale cliente</option>
                        <option value="dm">DM misto</option>
                        <option value="ticket">Ticket</option>
                    </select>
                    <Link to="/app/admin/audit-access" className="lt-btn-secondary" data-testid="admin-conv-audit-link">
                        <Filter size={14} /> Log accessi
                    </Link>
                </div>
                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
                        {activeFilters.map((c) => (
                            <span key={c.key} className="lt-pill" data-testid={`admin-conv-filter-chip-${c.key}`}>
                                {c.label}
                                <button type="button" onClick={c.clear} aria-label="Rimuovi filtro"><X size={12} /></button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* List */}
            <div data-testid="admin-conv-list">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 13, color: "#5F6560" }} data-testid="admin-conv-total">
                        {loading ? "Caricamento…" : `${count} conversazioni`}
                    </div>
                </div>
                <div className="lt-card" style={{ overflow: "hidden" }}>
                    {items.length === 0 && !loading ? (
                        <div style={{ padding: 32, textAlign: "center", color: "#8A8F88" }}>Nessuna conversazione con questi filtri.</div>
                    ) : items.map((it) => {
                        const Icon = TYPE_ICON[it.type] || MessageSquare;
                        return (
                            <Link
                                key={`${it.type}:${it.id}`}
                                to={`/app/admin/conversazioni/${it.type}/${it.id}`}
                                className="lt-thread-row"
                                data-testid={`admin-conv-row-${it.id}`}
                                style={{ display: "flex", padding: "14px 16px", borderBottom: "1px solid #EEEFEA", textDecoration: "none", color: "inherit" }}
                            >
                                <div style={{ width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "#F4F5F2", borderRadius: 6, marginRight: 12, flexShrink: 0 }}>
                                    <Icon size={16} color="#181B20" />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                                        <span style={{ fontSize: 14, fontWeight: 600, color: "#181B20", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                                        <span className="lt-badge" style={{ fontSize: 10, background: "#F4F5F2", color: "#5F6560", padding: "2px 6px", borderRadius: 3, textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: '"IBM Plex Mono", monospace' }}>
                                            {TYPE_LABEL[it.type] || it.type}
                                        </span>
                                        <span style={{ fontSize: 12, color: "#8A8F88" }}>· {it.org_name}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: "#5F6560", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {it.last_message_preview || <em style={{ color: "#B0B4AC" }}>Ancora nessun messaggio</em>}
                                    </div>
                                </div>
                                <div style={{ marginLeft: 16, textAlign: "right", flexShrink: 0 }}>
                                    <div style={{ fontSize: 12, color: "#5F6560" }}>{fmtDT(it.last_message_at)}</div>
                                    <div style={{ fontSize: 11, color: "#8A8F88", marginTop: 2, fontFamily: '"IBM Plex Mono", monospace', textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                        {it.message_count} msg
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                {count > PAGE && (
                    <div className="flex items-center justify-between" style={{ marginTop: 12 }}>
                        <button className="lt-btn-secondary" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))} data-testid="admin-conv-prev">
                            Precedenti
                        </button>
                        <div style={{ fontSize: 13, color: "#5F6560" }}>
                            {offset + 1}–{Math.min(offset + PAGE, count)} di {count}
                        </div>
                        <button className="lt-btn-secondary" disabled={offset + PAGE >= count} onClick={() => setOffset(offset + PAGE)} data-testid="admin-conv-next">
                            Successivi
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
